// Landmarks (styled from primitives) + the three Danube bridges.
import * as THREE from 'three';
import { aabb, C, facadeBox, facadeSoup, mat, type BuildCtx } from './city_common';
import { HALF, SIDEWALK, CURB_H, WATER_Y, type Block, type Region } from './city_layout';
import { addBuildingBox } from './city_buildings';
import { randRange } from './util';

const _c = new THREE.Color();
const tintOf = (hex: number) => _c.set(hex).clone();

function block(ctx: BuildCtx, key: string): Block {
  const b = ctx.layout.blocks.find((x) => x.landmark?.key === key);
  if (!b) throw new Error('landmark block missing: ' + key);
  return b;
}

/** POI on the sidewalk in front of the block (centre of the front side) */
function addPoi(ctx: BuildCtx, b: Block, r: Region, offset = 0): void {
  const cx = (b.x0 + b.x1) / 2 + (r.front === 'N' || r.front === 'S' ? offset : 0);
  const cz = (b.z0 + b.z1) / 2 + (r.front === 'E' || r.front === 'W' ? offset : 0);
  const s = SIDEWALK / 2;
  let pos: THREE.Vector3;
  let heading: number;
  switch (r.front) {
    case 'S':
      pos = new THREE.Vector3(cx, 0, b.z1 + s);
      heading = Math.PI;
      break;
    case 'N':
      pos = new THREE.Vector3(cx, 0, b.z0 - s);
      heading = 0;
      break;
    case 'E':
      pos = new THREE.Vector3(b.x1 + s, 0, cz);
      heading = -Math.PI / 2;
      break;
    default:
      pos = new THREE.Vector3(b.x0 - s, 0, cz);
      heading = Math.PI / 2;
  }
  ctx.pois.push({ key: r.key, name: r.name, pos, heading });
}

/** wall with arch openings; shape in local (x,y), extruded along +z by depth, centred on x=0, z in [0,depth] */
function archWall(w: number, h: number, depth: number, arches: { x: number; w: number; h: number }[]): THREE.BufferGeometry {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, 0);
  s.lineTo(w / 2, 0);
  s.lineTo(w / 2, h);
  s.lineTo(-w / 2, h);
  s.closePath();
  for (const a of arches) {
    const p = new THREE.Path();
    const r = a.w / 2;
    p.moveTo(a.x - r, 0);
    p.lineTo(a.x - r, a.h - r);
    p.absarc(a.x, a.h - r, r, Math.PI, 0, true);
    p.lineTo(a.x + r, 0);
    p.closePath();
    s.holes.push(p);
  }
  const g = new THREE.ExtrudeGeometry(s, { depth, bevelEnabled: false, curveSegments: 12 });
  return g;
}

function pitchedRoof(ctx: BuildCtx, x0: number, z0: number, x1: number, z1: number, y: number, ridgeH: number, alongX: boolean, color: number, overhang = 0.6): void {
  // two slopes as boxes rotated (cheap, closed volume look)
  const w = x1 - x0, d = z1 - z0;
  const cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
  if (alongX) {
    const half = d / 2 + overhang;
    const slope = Math.hypot(half, ridgeH);
    const ang = Math.atan2(ridgeH, half);
    ctx.solid.add(new THREE.BoxGeometry(w + overhang * 2, 0.25, slope), mat(cx, y + ridgeH / 2, cz + half / 2, 0, 1, 1, 1, ang), color);
    ctx.solid.add(new THREE.BoxGeometry(w + overhang * 2, 0.25, slope), mat(cx, y + ridgeH / 2, cz - half / 2, 0, 1, 1, 1, -ang), color);
    // gables
    const g = new THREE.Shape();
    g.moveTo(-d / 2, 0);
    g.lineTo(d / 2, 0);
    g.lineTo(0, ridgeH);
    g.closePath();
    const gg = new THREE.ShapeGeometry(g);
    ctx.solid.add(gg.clone(), mat(x1 - 0.05, y, cz, Math.PI / 2), color);
    ctx.solid.add(gg, mat(x0 + 0.05, y, cz, -Math.PI / 2), color);
  } else {
    const half = w / 2 + overhang;
    const slope = Math.hypot(half, ridgeH);
    const ang = Math.atan2(ridgeH, half);
    ctx.solid.add(new THREE.BoxGeometry(slope, 0.25, d + overhang * 2), mat(cx + half / 2, y + ridgeH / 2, cz, 0, 1, 1, 1, 0, -ang), color);
    ctx.solid.add(new THREE.BoxGeometry(slope, 0.25, d + overhang * 2), mat(cx - half / 2, y + ridgeH / 2, cz, 0, 1, 1, 1, 0, ang), color);
    const g = new THREE.Shape();
    g.moveTo(-w / 2, 0);
    g.lineTo(w / 2, 0);
    g.lineTo(0, ridgeH);
    g.closePath();
    const gg = new THREE.ShapeGeometry(g);
    ctx.solid.add(gg.clone(), mat(cx, y, z1 - 0.05, 0), color);
    ctx.solid.add(gg, mat(cx, y, z0 + 0.05, Math.PI), color);
  }
}

function flagPole(ctx: BuildCtx, x: number, z: number, h = 10): void {
  ctx.metal.cyl(x, 0, z, 0.06, 0.1, h, 0xc8ccd0, 6);
  ctx.solid.box(x + 0.7, h - 1.2, z, 1.4, 0.3, 0.03, 0xcd2a3e);
  ctx.solid.box(x + 0.7, h - 1.5, z, 1.4, 0.3, 0.03, 0xf0f0f0);
  ctx.solid.box(x + 0.7, h - 1.8, z, 1.4, 0.3, 0.03, 0x436f4d);
}

// ------------------------------------------------------------- parliament ----

function parliament(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  // terrace
  ctx.stoneLit.box(cx, CURB_H, cz, b.x1 - b.x0 - 2, 1.2, b.z1 - b.z0 - 2, C.stoneCream);
  const y0 = CURB_H + 1.2;
  const bx0 = cx - 18, bx1 = cx + 14, bz0 = cz - 54, bz1 = cz + 54;
  const h = y0 + 4 * F.gothic.floorH;
  facadeBox(facadeSoup(ctx, 'gothic'), F.gothic, bx0, bz0, bx1, bz1, y0, h, 0, 0, tintOf(0xffffff));
  ctx.stoneLit.box(cx - 2, h, cz, bx1 - bx0 + 1.2, 0.8, bz1 - bz0 + 1.2, C.stoneCream);
  // river-side lower gallery with arches
  const gal = archWall(96, 7, 4, Array.from({ length: 11 }, (_, i) => ({ x: -40 + i * 8, w: 3.6, h: 5.5 })));
  ctx.stoneLit.add(gal, mat(bx0, y0, cz, -Math.PI / 2), C.stoneCream);
  ctx.stoneLit.box(bx0 - 2, y0 + 7, cz, 4.4, 0.6, 96.6, C.stoneCream);
  // pitched roof
  pitchedRoof(ctx, bx0 + 0.5, bz0 + 0.5, bx1 - 0.5, bz1 - 0.5, h + 0.8, 7, false, C.roofRed, 0.4);
  // central dome on a drum
  const dcx = cx - 2;
  ctx.stoneLit.cyl(dcx, h, cz, 11, 11.5, 9, C.stoneCream, 24);
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    ctx.stoneLit.box(dcx + Math.cos(a) * 11.3, h, cz + Math.sin(a) * 11.3, 1.2, 9, 1.2, C.white, -a);
  }
  ctx.stoneLit.box(dcx, h + 9, cz, 24, 0.8, 24, C.stoneCream);
  ctx.solid.dome(dcx, h + 9.8, cz, 11, C.roofRed, 28, 1.35);
  ctx.solid.cone(dcx, h + 9.8 + 14.8, cz, 2.6, 16, C.roofRed, 10);
  ctx.metal.cyl(dcx, h + 9.8 + 30.5, cz, 0.15, 0.3, 4, 0xd4b060, 6);
  // corner towers + mid towers with pointed roofs
  const towers: [number, number, number, number][] = [
    [bx0 + 3, bz0 + 3, 7, 32],
    [bx1 - 3, bz0 + 3, 7, 32],
    [bx0 + 3, bz1 - 3, 7, 32],
    [bx1 - 3, bz1 - 3, 7, 32],
    [dcx, bz0 + 14, 8, 36],
    [dcx, bz1 - 14, 8, 36],
  ];
  for (const [tx, tz, tw, th] of towers) {
    ctx.stoneLit.box(tx, y0, tz, tw, th, tw, C.stoneCream);
    ctx.stoneLit.box(tx, y0 + th, tz, tw + 1, 0.7, tw + 1, C.stoneCream);
    ctx.solid.cone(tx, y0 + th + 0.7, tz, tw * 0.62, tw * 1.6, C.roofRed, 8);
    for (const [ox, oz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) ctx.solid.cone(tx + (ox * tw) / 2, y0 + th + 0.7, tz + (oz * tw) / 2, 0.6, 3, C.roofRed, 6);
  }
  // small spires along the long sides
  for (let z = bz0 + 10; z < bz1 - 8; z += 8) {
    for (const x of [bx0 + 0.3, bx1 - 0.3]) {
      ctx.stoneLit.box(x, h - 2, z, 1.4, 4.5, 1.4, C.stoneCream);
      ctx.solid.cone(x, h + 2.5, z, 0.9, 4, C.roofRed, 6);
    }
  }
  // plaza in front (east) with flags + statue plinth
  for (let i = 0; i < 4; i++) flagPole(ctx, b.x1 - 3, cz - 30 + i * 20, 12);
  ctx.stoneLit.box(b.x1 - 9, y0, cz, 3, 2.5, 3, C.stoneGrey);
  ctx.solid.box(b.x1 - 9, y0 + 2.5, cz, 1.2, 3.2, 1.2, 0x2f4a3a);
  ctx.colliders.push(aabb(b.x0 + 1, b.z0 + 1, b.x1 - 1, b.z1 - 1, 1.3));
  ctx.colliders.push(aabb(bx0 - 4.5, bz0 - 0.5, bx1 + 0.5, bz1 + 0.5, h));
  ctx.buildingRects.push(aabb(bx0 - 4, bz0, bx1, bz1, h));
  addPoi(ctx, b, b.landmark!);
}

// ------------------------------------------------------------------ castle ----

function castle(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  const w = b.x1 - b.x0, d = b.z1 - b.z0;
  // lower terrace with ramparts
  ctx.stoneLit.box(cx, CURB_H, cz, w, 3.2, d, C.stoneGrey);
  const ring = (x0: number, z0: number, x1: number, z1: number, y: number, wallH: number) => {
    const t = 0.9;
    ctx.stoneLit.box((x0 + x1) / 2, y, z0 + t / 2, x1 - x0, wallH, t, C.stoneGrey);
    ctx.stoneLit.box((x0 + x1) / 2, y, z1 - t / 2, x1 - x0, wallH, t, C.stoneGrey);
    ctx.stoneLit.box(x0 + t / 2, y, (z0 + z1) / 2, t, wallH, z1 - z0, C.stoneGrey);
    ctx.stoneLit.box(x1 - t / 2, y, (z0 + z1) / 2, t, wallH, z1 - z0, C.stoneGrey);
    // merlons
    for (let x = x0 + 1; x < x1 - 1; x += 2.4) {
      ctx.stoneLit.box(x, y + wallH, z0 + t / 2, 1.1, 0.7, t, C.stoneGrey);
      ctx.stoneLit.box(x, y + wallH, z1 - t / 2, 1.1, 0.7, t, C.stoneGrey);
    }
    for (let z = z0 + 1; z < z1 - 1; z += 2.4) {
      ctx.stoneLit.box(x0 + t / 2, y + wallH, z, t, 0.7, 1.1, C.stoneGrey);
      ctx.stoneLit.box(x1 - t / 2, y + wallH, z, t, 0.7, 1.1, C.stoneGrey);
    }
  };
  ring(b.x0, b.z0, b.x1, b.z1, CURB_H + 3.2, 1.3);
  // corner bastions
  for (const [x, z] of [[b.x0 + 3, b.z0 + 3], [b.x1 - 3, b.z0 + 3], [b.x0 + 3, b.z1 - 3], [b.x1 - 3, b.z1 - 3]]) {
    ctx.stoneLit.cyl(x, CURB_H, z, 4.2, 4.6, 7.5, C.stoneGrey, 16);
    ctx.solid.cone(x, CURB_H + 7.5, z, 4.6, 4, C.roofRed, 16);
  }
  // upper terrace
  const ux0 = b.x0 + 6, ux1 = b.x1 - 6, uz0 = b.z0 + 10, uz1 = b.z1 - 10;
  ctx.stoneLit.box(cx, CURB_H + 3.2, cz, ux1 - ux0, 3.6, uz1 - uz0, C.stoneGrey);
  ring(ux0, uz0, ux1, uz1, CURB_H + 6.8, 1.1);
  const y0 = CURB_H + 6.8;
  // palace
  const px0 = cx - 12, px1 = cx + 12, pz0 = cz - 40, pz1 = cz + 40;
  const ph = y0 + 4 * F.stone.floorH;
  facadeBox(facadeSoup(ctx, 'stone'), F.stone, px0, pz0, px1, pz1, y0, ph, 0, 0, tintOf(0xf4e8c8));
  ctx.stoneLit.box(cx, ph, cz, px1 - px0 + 1.2, 0.8, pz1 - pz0 + 1.2, C.stoneCream);
  ctx.roofs.floor(px0, pz0, px1, pz1, ph + 0.8, 12);
  // wing pavilions with pyramid roofs
  for (const z of [pz0 + 6, pz1 - 6]) {
    ctx.stoneLit.box(cx, y0, z, 26, ph - y0 + 3, 12, C.stoneCream);
    ctx.solid.add(new THREE.ConeGeometry(9.5, 6, 4), mat(cx, ph + 6, z, Math.PI / 4, 1.42, 1, 0.7), C.roofDark);
  }
  // central dome
  ctx.stoneLit.cyl(cx, ph + 0.8, cz, 8, 8.5, 5, C.stoneCream, 20);
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    ctx.stoneLit.box(cx + Math.cos(a) * 8.3, ph + 0.8, cz + Math.sin(a) * 8.3, 1, 5, 1, C.white, -a);
  }
  ctx.solid.dome(cx, ph + 5.8, cz, 8.4, C.copperGreen, 24, 1.05);
  ctx.stoneLit.cyl(cx, ph + 14.4, cz, 1.6, 1.8, 3, C.stoneCream, 10);
  ctx.solid.dome(cx, ph + 17.4, cz, 1.8, C.copperGreen, 10);
  flagPole(ctx, cx + 0.1, cz, 6);
  // grand stairs on the river (east) side leading to the entrance gate
  for (let i = 0; i < 6; i++) ctx.stoneLit.box(b.x1 - 3 - i * 0.9, CURB_H, cz, 1.0, 3.2 - i * 0.5, 14, C.stoneGrey);
  ctx.colliders.push(aabb(b.x0, b.z0, b.x1, b.z1, 5));
  ctx.colliders.push(aabb(px0, pz0, px1, pz1, ph));
  ctx.buildingRects.push(aabb(b.x0, b.z0, b.x1, b.z1, ph));
  addPoi(ctx, b, b.landmark!);
}

// ---------------------------------------------------------------- basilica ----

function basilica(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  const y0 = CURB_H + 1.4;
  // terrace + steps
  ctx.stoneLit.box(cx, CURB_H, cz - 2, 40, 1.4, 44, C.stoneCream);
  for (let i = 0; i < 4; i++) ctx.stoneLit.box(cx, CURB_H, cz + 20 + i * 0.9, 24 - i * 0.6, 1.4 - i * 0.35, 1.0, C.stoneCream);
  // nave
  const nx0 = cx - 14, nx1 = cx + 14, nz0 = cz - 22, nz1 = cz + 16;
  const nh = y0 + 3 * F.stone.floorH;
  facadeBox(facadeSoup(ctx, 'stone'), F.stone, nx0, nz0, nx1, nz1, y0, nh, 0, 0, tintOf(0xffffff));
  ctx.stoneLit.box(cx, nh, cz - 3, 29.2, 0.9, 39.2, C.stoneCream);
  pitchedRoof(ctx, nx0, nz0, nx1, nz1, nh + 0.9, 5, false, C.roofDark, 0.3);
  // portico
  for (let i = 0; i < 6; i++) ctx.stoneLit.cyl(cx - 10 + i * 4, y0, cz + 18.5, 0.7, 0.8, 11, C.white, 12);
  ctx.stoneLit.box(cx, y0 + 11, cz + 17.5, 26, 1.4, 6, C.stoneCream);
  ctx.stoneLit.add(new THREE.ShapeGeometry(triangle(26, 4.5)), mat(cx, y0 + 12.4, cz + 20.4, 0), C.stoneCream);
  ctx.stoneLit.box(cx, y0 + 12.4, cz + 17.5, 26, 0.3, 6, C.stoneCream);
  // front towers
  for (const tx of [cx - 11, cx + 11]) {
    const th = y0 + 8 * F.stone.floorH;
    facadeBox(facadeSoup(ctx, 'stone'), F.stone, tx - 4, cz + 10, tx + 4, cz + 18, y0, th, 4, 0, tintOf(0xffffff));
    ctx.stoneLit.box(tx, th, cz + 14, 9.2, 0.8, 9.2, C.stoneCream);
    ctx.stoneLit.cyl(tx, th + 0.8, cz + 14, 3.2, 3.6, 3, C.stoneCream, 12);
    ctx.solid.dome(tx, th + 3.8, cz + 14, 3.6, C.copperGreen, 16, 1.5);
    ctx.metal.box(tx, th + 9.5, cz + 14, 0.25, 2.6, 0.25, 0xd4b060);
    ctx.metal.box(tx, th + 11.2, cz + 14, 1.4, 0.25, 0.25, 0xd4b060);
    ctx.colliders.push(aabb(tx - 4, cz + 10, tx + 4, cz + 18, th));
  }
  // dome
  const dz = cz - 4;
  ctx.stoneLit.cyl(cx, nh + 0.9, dz, 11, 11.5, 8, C.stoneCream, 28);
  for (let i = 0; i < 20; i++) {
    const a = (i / 20) * Math.PI * 2;
    ctx.stoneLit.box(cx + Math.cos(a) * 11.4, nh + 0.9, dz + Math.sin(a) * 11.4, 1.1, 8, 1.1, C.white, -a);
  }
  ctx.stoneLit.box(cx, nh + 8.9, dz, 25, 0.8, 25, C.stoneCream);
  ctx.solid.dome(cx, nh + 9.7, dz, 11.5, C.copperGreen, 32, 1.15);
  ctx.stoneLit.cyl(cx, nh + 9.7 + 13.2, dz, 2.2, 2.4, 4.5, C.stoneCream, 12);
  ctx.solid.dome(cx, nh + 9.7 + 17.7, dz, 2.6, C.copperGreen, 12);
  ctx.metal.box(cx, nh + 9.7 + 20.3, dz, 0.3, 3.5, 0.3, 0xd4b060);
  ctx.metal.box(cx, nh + 9.7 + 22.6, dz, 1.8, 0.3, 0.3, 0xd4b060);
  ctx.colliders.push(aabb(nx0, nz0, nx1, nz1 + 6, nh));
  ctx.colliders.push(aabb(cx - 20, cz - 24, cx + 20, cz + 20, 1.5));
  ctx.buildingRects.push(aabb(nx0, nz0, nx1, nz1 + 4, nh));
  addPoi(ctx, b, b.landmark!);
}

function triangle(w: number, h: number): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(-w / 2, 0);
  s.lineTo(w / 2, 0);
  s.lineTo(0, h);
  s.closePath();
  return s;
}

// ------------------------------------------------------------------ market ----

function market(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  const hx0 = b.x0 + 3, hx1 = b.x1 - 10, hz0 = cz - 15, hz1 = cz + 15;
  const hh = CURB_H + 4 * F.brick.floorH;
  facadeBox(facadeSoup(ctx, 'brick'), F.brick, hx0, hz0, hx1, hz1, CURB_H, hh, 0, 0, tintOf(0xffffff));
  ctx.solid.box((hx0 + hx1) / 2, hh, cz, hx1 - hx0 + 1, 0.7, hz1 - hz0 + 1, 0xd6cbb8);
  // patterned pitched roof (two quads in the market soup + gables)
  const ridge = 8;
  const y = hh + 0.7;
  const over = 0.8;
  const S = ctx.marketSoup;
  const tile = 6;
  S.quad(hx0 - over, y, hz1 + over, hx1 + over, y, hz1 + over, hx1 + over, y + ridge, cz, hx0 - over, y + ridge, cz, 0, 0, (hx1 - hx0) / tile, 0, (hx1 - hx0) / tile, 3, 0, 3);
  S.quad(hx1 + over, y, hz0 - over, hx0 - over, y, hz0 - over, hx0 - over, y + ridge, cz, hx1 + over, y + ridge, cz, 0, 0, (hx1 - hx0) / tile, 0, (hx1 - hx0) / tile, 3, 0, 3);
  const g = new THREE.ShapeGeometry(triangle(hz1 - hz0, ridge));
  ctx.solid.add(g.clone(), mat(hx1 - 0.05, y, cz, Math.PI / 2), 0x8a4a3a);
  ctx.solid.add(g, mat(hx0 + 0.05, y, cz, -Math.PI / 2), 0x8a4a3a);
  // roof lantern (clerestory)
  ctx.glass.box((hx0 + hx1) / 2, y + ridge - 1.5, cz, hx1 - hx0 - 8, 2.2, 5, 0x9ab8cc);
  ctx.solid.box((hx0 + hx1) / 2, y + ridge + 0.7, cz, hx1 - hx0 - 7, 0.4, 6, 0x2f7d4f);
  // entrance pavilion on the east end with a big arch
  const px = hx1 + 4;
  ctx.solid.box(px - 0.3, CURB_H, cz, 7.4, 17, 22, 0x9a5240);
  ctx.solid.add(archWall(22, 17.5, 1.2, [{ x: 0, w: 8, h: 11 }]), mat(px + 4, CURB_H, cz, Math.PI / 2), 0xd6cbb8);
  ctx.lit.box(px + 3.6, CURB_H, cz, 0.2, 10.5, 7.6, 0xffe0b0);
  ctx.solid.box(px, CURB_H + 17, cz, 9, 0.8, 23, 0xd6cbb8);
  pitchedRoof(ctx, px - 4, cz - 11, px + 4, cz + 11, CURB_H + 17.8, 4, true, 0x2f7d4f, 0.3);
  for (const z of [cz - 12, cz + 12]) {
    ctx.solid.cyl(px + 2, CURB_H, z, 1.8, 2, 21, 0x9a5240, 12);
    ctx.solid.cone(px + 2, CURB_H + 21, z, 2.2, 5, 0x2f7d4f, 12);
  }
  ctx.signRequests.push({ x: px + 5.3, y: 14.5, z: cz, ry: Math.PI / 2, w: 14, idx: 4 });
  // stalls on the plaza
  for (let i = 0; i < 3; i++) {
    const sz = cz - 16 + i * 16;
    const sx = b.x1 - 3;
    ctx.solid.box(sx, CURB_H, sz, 2.2, 0.9, 3, 0x6b4a2e);
    for (const dx of [-1, 1]) ctx.metal.cyl(sx + dx, CURB_H, sz - 1.4, 0.04, 0.04, 2.4, 0x333333, 6);
    for (const dx of [-1, 1]) ctx.metal.cyl(sx + dx, CURB_H, sz + 1.4, 0.04, 0.04, 2.4, 0x333333, 6);
    ctx.solid.add(new THREE.BoxGeometry(2.8, 0.06, 3.4), mat(sx, CURB_H + 2.4, sz, 0, 1, 1, 1, 0, 0.25), i % 2 ? 0x8b1e2d : 0x2d5a27);
    ctx.colliders.push(aabb(sx - 1.1, sz - 1.5, sx + 1.1, sz + 1.5, 1));
  }
  ctx.colliders.push(aabb(hx0, hz0, px + 4, hz1, hh));
  ctx.colliders.push(aabb(px - 4, cz - 13, px + 4.2, cz + 13, 17));
  ctx.buildingRects.push(aabb(hx0, hz0, px + 4, hz1, hh));
  addPoi(ctx, b, b.landmark!, 12);
}

// ----------------------------------------------------------------- station ----

function station(ctx: BuildCtx, b: Block): void {
  const cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  const fx0 = b.x0 + 1, fx1 = b.x0 + 15;
  const y0 = CURB_H + 1;
  const wh = y0 + 3 * F.stone.floorH;
  // side wings
  facadeBox(facadeSoup(ctx, 'stone'), F.stone, fx0, cz - 23, fx1, cz - 9, y0, wh, 2, 0, tintOf(0xf4e8c8));
  facadeBox(facadeSoup(ctx, 'stone'), F.stone, fx0, cz + 9, fx1, cz + 23, y0, wh, 6, 0, tintOf(0xf4e8c8));
  ctx.stoneLit.box((fx0 + fx1) / 2, wh, cz - 16, fx1 - fx0 + 1, 0.8, 15, C.stoneCream);
  ctx.stoneLit.box((fx0 + fx1) / 2, wh, cz + 16, fx1 - fx0 + 1, 0.8, 15, C.stoneCream);
  ctx.stoneLit.box((fx0 + fx1) / 2, CURB_H, cz, fx1 - fx0 + 2, 1, 48, C.stoneCream);
  // central hall with the big arch window
  const hallH = 25;
  ctx.stoneLit.box((fx0 + 2.4 + fx1) / 2, y0, cz, fx1 - fx0 - 2.4, hallH, 18, C.stoneCream);
  ctx.stoneLit.add(archWall(20, hallH, 1.6, [{ x: 0, w: 13, h: 19 }]), mat(fx0 + 1.6, y0, cz, -Math.PI / 2), C.stoneCream);
  ctx.glass.box(fx0 + 1.85, y0, cz, 0.2, 19, 13, 0x6a8fb0);
  ctx.lit.box(fx0 + 2.2, y0, cz, 0.1, 18.5, 12.5, 0xffd9a0);
  // arch top cornice + clock + statues
  ctx.stoneLit.box((fx0 + fx1) / 2 + 1, y0 + hallH, cz, fx1 - fx0, 1.2, 20, C.stoneCream);
  ctx.solid.cyl(fx0 + 0.6, y0 + hallH - 4, cz, 1.6, 1.6, 0.3, 0xf6f2e8, 20);
  ctx.metal.box(fx0 + 0.3, y0 + hallH - 4, cz, 0.1, 2.4, 0.15, 0x222222);
  ctx.metal.box(fx0 + 0.3, y0 + hallH - 4, cz + 0.5, 0.1, 0.15, 1.2, 0x222222);
  // corner towers with domes
  for (const tz of [cz - 10, cz + 10]) {
    ctx.stoneLit.box(fx0 + 4, y0, tz, 7, hallH + 4, 7, C.stoneCream);
    ctx.stoneLit.box(fx0 + 4, y0 + hallH + 4, tz, 8, 0.8, 8, C.stoneCream);
    ctx.solid.dome(fx0 + 4, y0 + hallH + 4.8, tz, 4, C.copperGreen, 16, 1.2);
    ctx.metal.box(fx0 + 4, y0 + hallH + 9.6, tz, 0.2, 2.5, 0.2, 0xd4b060);
  }
  ctx.signRequests.push({ x: fx0 - 0.4, y: y0 + hallH - 1.4, z: cz, ry: -Math.PI / 2, w: 13, idx: 3 });
  // train shed: brick walls + glass barrel vault with steel ribs
  const sx0 = fx1, sx1 = b.x1 - 1;
  const sd = 30;
  ctx.solid.box((sx0 + sx1) / 2, CURB_H, cz, sx1 - sx0, 7, sd, 0x8a5240);
  const vault = new THREE.CylinderGeometry(sd / 2, sd / 2, sx1 - sx0, 28, 1, true, 0, Math.PI);
  ctx.glass.add(vault, mat((sx0 + sx1) / 2, CURB_H + 7, cz, 0, 0.7, 1, 1, 0, Math.PI / 2), 0x8fb3cc);
  for (let x = sx0 + 3; x < sx1; x += 6) {
    ctx.metal.add(new THREE.TorusGeometry(sd / 2, 0.25, 6, 20, Math.PI), mat(x, CURB_H + 7, cz, Math.PI / 2, 1, 0.7, 1, 0, 0), 0x2f6b4f);
  }
  ctx.solid.box(sx1 - 0.3, CURB_H, cz, 0.6, 7, sd, 0x8a5240);
  // tracks stub + platform
  ctx.solid.box(sx1 - 6, CURB_H, cz, 10, 0.5, 6, 0x9a9892);
  ctx.colliders.push(aabb(fx0 - 1, cz - 24, fx1 + 1, cz + 24, wh));
  ctx.colliders.push(aabb(sx0, cz - sd / 2, sx1, cz + sd / 2, 7));
  ctx.buildingRects.push(aabb(fx0, cz - 24, sx1, cz + 24, wh));
  addPoi(ctx, b, b.landmark!);
}

// ----------------------------------------------------------------- ruin bar ----

function ruinbar(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2;
  const F = ctx.tex.facades;
  const rng = ctx.rng;
  const wings: { x0: number; z0: number; x1: number; z1: number; st: string; floors: number; sides: string }[] = [
    { x0: b.x0, z0: b.z0, x1: b.x1, z1: b.z0 + 14, st: 'brick', floors: 3, sides: 'N' },
    { x0: b.x0, z0: b.z0 + 14, x1: b.x0 + 14, z1: b.z1, st: 'terracotta', floors: 2, sides: 'W' },
    { x0: b.x1 - 14, z0: b.z0 + 14, x1: b.x1, z1: b.z1, st: 'classic', floors: 3, sides: 'E' },
    { x0: b.x0 + 14, z0: b.z1 - 12, x1: cx - 5, z1: b.z1, st: 'brick', floors: 2, sides: 'S' },
    { x0: cx + 5, z0: b.z1 - 12, x1: b.x1 - 14, z1: b.z1, st: 'panel', floors: 3, sides: 'S' },
  ];
  const heights: number[] = [];
  for (const w of wings) {
    heights.push(addBuildingBox(ctx, w.x0, w.z0, w.x1, w.z1, { style: F[w.st], shops: false, floors: w.floors, streetSides: w.sides, plain: true }));
  }
  // entrance gate: header beam between the two south buildings
  ctx.metal.box(cx, 5.5, b.z1 - 1, 10.4, 0.5, 0.4, 0x3a3d42);
  ctx.metal.box(cx - 5.1, 0, b.z1 - 1, 0.3, 5.5, 0.3, 0x3a3d42);
  ctx.metal.box(cx + 5.1, 0, b.z1 - 1, 0.3, 5.5, 0.3, 0x3a3d42);
  // neon signs on the street faces
  const neon = ctx.neonRequests;
  neon.push({ x: cx, y: 6.4, z: b.z1 + 0.25, ry: 0, w: 9, idx: 1 });
  neon.push({ x: cx - 12, y: 4.5, z: b.z1 + 0.2, ry: 0, w: 5, idx: 0 });
  neon.push({ x: cx + 12, y: 5.2, z: b.z1 + 0.2, ry: 0, w: 5, idx: 2 });
  neon.push({ x: b.x1 + 0.2, y: 5.5, z: b.z0 + 30, ry: Math.PI / 2, w: 6, idx: 3 });
  neon.push({ x: b.x0 - 0.2, y: 4.8, z: b.z0 + 30, ry: -Math.PI / 2, w: 6, idx: 4 });
  neon.push({ x: cx - 8, y: 7.5, z: b.z0 - 0.2, ry: Math.PI, w: 6, idx: 5 });
  neon.push({ x: cx + 9, y: 6.0, z: b.z0 - 0.2, ry: Math.PI, w: 6, idx: 6 });
  // courtyard: signs inside, string lights, furniture
  const cy0 = b.z0 + 14, cy1 = b.z1 - 12, cxx0 = b.x0 + 14, cxx1 = b.x1 - 14;
  neon.push({ x: (cxx0 + cxx1) / 2, y: 5, z: cy0 + 0.2, ry: 0, w: 7, idx: 7 });
  neon.push({ x: cxx0 + 0.2, y: 4, z: (cy0 + cy1) / 2, ry: Math.PI / 2, w: 5, idx: 2 });
  neon.push({ x: cxx1 - 0.2, y: 4.5, z: (cy0 + cy1) / 2, ry: -Math.PI / 2, w: 5, idx: 5 });
  for (let i = 0; i < 5; i++) {
    const z = cy0 + 3 + i * ((cy1 - cy0 - 6) / 4);
    const yA = 5.2, yB = 5.6;
    const n = 12;
    for (let k = 0; k <= n; k++) {
      const t = k / n;
      const x = cxx0 + (cxx1 - cxx0) * t;
      const y = yA + (yB - yA) * t - Math.sin(t * Math.PI) * 0.8;
      ctx.bulbRequests.push({ x, y: y - 0.15, z });
      if (k < n) {
        const t2 = (k + 1) / n;
        ctx.wires.push(x, y, z, cxx0 + (cxx1 - cxx0) * t2, yA + (yB - yA) * t2 - Math.sin(t2 * Math.PI) * 0.8, z);
      }
    }
  }
  // tables, benches, bar counter, drums, planters
  for (let i = 0; i < 7; i++) {
    const x = randRange(rng, cxx0 + 3, cxx1 - 3), z = randRange(rng, cy0 + 3, cy1 - 3);
    ctx.solid.cyl(x, CURB_H, z, 0.55, 0.55, 0.08, 0x6b4a2e, 12);
    ctx.metal.cyl(x, CURB_H, z, 0.05, 0.05, 0.75, 0x333333, 6);
    ctx.solid.cyl(x, CURB_H + 0.75, z, 0.5, 0.5, 0.05, 0x8b1e2d, 12);
    ctx.colliders.push(aabb(x - 0.5, z - 0.5, x + 0.5, z + 0.5, 0.8));
  }
  ctx.solid.box(cxx0 + 4, CURB_H, cy0 + 3, 7, 1.1, 1.2, 0x4a3626);
  ctx.solid.box(cxx0 + 4, CURB_H + 1.1, cy0 + 3, 7.4, 0.1, 1.5, 0x2a2a2a);
  ctx.colliders.push(aabb(cxx0 + 0.5, cy0 + 2.4, cxx0 + 7.5, cy0 + 3.6, 1.2));
  for (let i = 0; i < 4; i++) ctx.metal.cyl(cxx1 - 2, CURB_H, cy0 + 2 + i * 1.1, 0.35, 0.35, 0.9, [0x8b1e2d, 0x2d5a27, 0x1e3a5f, 0xb8860b][i], 10);
  ctx.benchRequests.push({ x: (cxx0 + cxx1) / 2, z: cy1 - 2, ry: Math.PI });
  ctx.benchRequests.push({ x: cxx0 + 2, z: (cy0 + cy1) / 2, ry: Math.PI / 2 });
  ctx.treeRequests.push({ x: (cxx0 + cxx1) / 2 + 4, z: (cy0 + cy1) / 2, s: 1.1 });
  // graffiti panels
  for (let i = 0; i < 4; i++) {
    ctx.solid.box(cxx0 + 0.2, CURB_H + 1 + i * 0.1, cy0 + 8 + i * 4, 0.1, 2.2, 3, [0xe0433a, 0x2fa7d8, 0xf2c322, 0x8b3fb8][i]);
  }
  void heights;
  ctx.buildingRects.push(aabb(b.x0, b.z0, b.x1, b.z1, 10));
  addPoi(ctx, b, b.landmark!);
}

// -------------------------------------------------------------------- bath ----

function bath(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  const y0 = CURB_H + 1;
  const bx0 = b.x0 + 2, bx1 = cx + 12, bz0 = cz - 21, bz1 = cz + 21;
  ctx.stoneLit.box((bx0 + bx1) / 2, CURB_H, cz, bx1 - bx0 + 2, 1, bz1 - bz0 + 2, C.stoneCream);
  const h = y0 + 3 * F.stone.floorH;
  facadeBox(facadeSoup(ctx, 'stone'), F.stone, bx0, bz0, bx1, bz1, y0, h, 0, 0, tintOf(0xffd870));
  ctx.stoneLit.box((bx0 + bx1) / 2, h, cz, bx1 - bx0 + 1.2, 0.9, bz1 - bz0 + 1.2, C.stoneCream);
  ctx.roofs.floor(bx0, bz0, bx1, bz1, h + 0.9, 12);
  // balustrade
  for (let z = bz0 + 1; z < bz1; z += 1.5) for (const x of [bx0 - 0.3, bx1 + 0.3]) ctx.stoneLit.box(x, h + 0.9, z, 0.3, 0.9, 0.3, C.stoneCream);
  // central dome + 4 small
  const dcx = (bx0 + bx1) / 2;
  ctx.stoneLit.cyl(dcx, h + 0.9, cz, 8, 8.5, 4, C.stoneCream, 20);
  ctx.solid.dome(dcx, h + 4.9, cz, 8.4, C.copperGreen, 24, 1.1);
  ctx.stoneLit.cyl(dcx, h + 4.9 + 9.2, cz, 1.4, 1.6, 2.5, C.stoneCream, 10);
  ctx.solid.dome(dcx, h + 4.9 + 11.7, cz, 1.6, C.copperGreen, 10);
  for (const [x, z] of [[bx0 + 5, bz0 + 5], [bx1 - 5, bz0 + 5], [bx0 + 5, bz1 - 5], [bx1 - 5, bz1 - 5]]) {
    ctx.stoneLit.cyl(x, h + 0.9, z, 3.4, 3.7, 2.5, C.stoneCream, 14);
    ctx.solid.dome(x, h + 3.4, z, 3.6, C.copperGreen, 16, 1.1);
  }
  // entrance arcade on the east
  ctx.stoneLit.add(archWall(30, 9, 3, Array.from({ length: 5 }, (_, i) => ({ x: -12 + i * 6, w: 3.2, h: 7 }))), mat(bx1, y0, cz, Math.PI / 2), C.stoneCream);
  ctx.stoneLit.box(bx1 + 1.5, y0 + 9, cz, 3.4, 0.6, 30.6, C.stoneCream);
  ctx.signRequests.push({ x: bx1 + 3.3, y: y0 + 11.5, z: cz, ry: Math.PI / 2, w: 12, idx: 5 });
  // outdoor pool on the east plaza
  const px0 = bx1 + 6, px1 = b.x1 - 2, pz0 = cz - 12, pz1 = cz + 12;
  ctx.stoneLit.box((px0 + px1) / 2, CURB_H, (pz0 + pz1) / 2, px1 - px0 + 2, 0.5, pz1 - pz0 + 2, C.stoneCream);
  ctx.glass.box((px0 + px1) / 2, CURB_H + 0.35, (pz0 + pz1) / 2, px1 - px0, 0.1, pz1 - pz0, 0x2fb8c8);
  ctx.colliders.push(aabb(px0 - 1, pz0 - 1, px1 + 1, pz1 + 1, 0.6));
  for (let i = 0; i < 4; i++) ctx.treeRequests.push({ x: px0 + (i % 2) * (px1 - px0), z: i < 2 ? pz0 - 3 : pz1 + 3, s: 0.9 });
  ctx.colliders.push(aabb(bx0 - 1, bz0 - 1, bx1 + 4.5, bz1 + 1, h));
  ctx.buildingRects.push(aabb(bx0, bz0, bx1 + 3, bz1, h));
  addPoi(ctx, b, b.landmark!);
}

// ----------------------------------------------------------------- stadium ----

function stadium(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const rx = 24, rz = 58, H = 17;
  // outer wall (ellipse) + inner tier
  ctx.solid.add(new THREE.CylinderGeometry(1, 1, 1, 56, 1, true), mat(cx, CURB_H + H / 2, cz, 0, rx, H, rz), 0xb4b2ae);
  ctx.solid.add(new THREE.CylinderGeometry(1.01, 1.01, 1, 56, 1, true), mat(cx, CURB_H + H - 4.5, cz, 0, rx, 2.2, rz), 0xa4102a);
  const tier = new THREE.CylinderGeometry(0.62, 0.97, 1, 56, 1, true);
  ctx.solid.add(tier, mat(cx, CURB_H + 3 + 6.5, cz, 0, rx, 13, rz), 0x9a2030);
  const ringG = new THREE.RingGeometry(0.72, 1.03, 56);
  ringG.rotateX(-Math.PI / 2);
  ctx.solid.add(ringG, mat(cx, CURB_H + H, cz, 0, rx, 1, rz), 0xe8e8e4);
  // pilasters
  for (let i = 0; i < 28; i++) {
    const a = (i / 28) * Math.PI * 2;
    ctx.solid.box(cx + Math.cos(a) * rx * 1.01, CURB_H, cz + Math.sin(a) * rz * 1.01, 1.4, H, 1.4, 0x8e8c88, -a);
  }
  // pitch
  ctx.parkSoup.floor(cx - 13, cz - 33, cx + 13, cz + 33, CURB_H + 0.02, 8);
  // gates on the west + sign
  for (const dz of [-14, 0, 14]) ctx.solid.box(cx - rx + 0.2, CURB_H, cz + dz, 1.2, 5, 6, 0x2a2a2e);
  ctx.signRequests.push({ x: cx - rx - 0.9, y: CURB_H + 9.5, z: cz, ry: -Math.PI / 2, w: 16, idx: 6 });
  // floodlight masts
  for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
    const x = cx + sx * (rx + 4), z = cz + sz * (rz - 6);
    ctx.metal.cyl(x, CURB_H, z, 0.35, 0.55, 34, 0x8a8f96, 8);
    ctx.metal.box(x, CURB_H + 34, z, 4.5, 3, 0.8, 0x50545a, Math.atan2(cx - x, cz - z));
    ctx.lit.box(x, CURB_H + 34.2, z, 4.2, 2.6, 0.9, 0xffffff, Math.atan2(cx - x, cz - z));
    ctx.colliders.push(aabb(x - 0.6, z - 0.6, x + 0.6, z + 0.6, 34));
  }
  ctx.colliders.push(aabb(cx - rx, cz - rz, cx + rx, cz + rz, H));
  ctx.buildingRects.push(aabb(cx - rx, cz - rz, cx + rx, cz + rz, H));
  addPoi(ctx, b, b.landmark!);
}

// ------------------------------------------------------------------- docks ----

function docks(ctx: BuildCtx, b: Block): void {
  const cz = (b.z0 + b.z1) / 2;
  const rng = ctx.rng;
  const L = ctx.layout;
  // warehouses along the south edge
  for (let i = 0; i < 3; i++) {
    const wx = b.x0 + 22 + i * 38;
    const wz = b.z1 - 9;
    ctx.solid.box(wx, CURB_H, wz, 30, 7, 16, [0x8a8f94, 0x9a8f80, 0x7f8a92][i]);
    for (let k = -13; k <= 13; k += 2) ctx.solid.box(wx + k, CURB_H, wz - 8.05, 0.3, 7, 0.3, 0x6f7479);
    ctx.solid.box(wx - 8, CURB_H, wz - 8.1, 6, 5, 0.3, 0x3a3f44);
    ctx.solid.box(wx + 8, CURB_H, wz - 8.1, 6, 5, 0.3, 0x3a3f44);
    pitchedRoof(ctx, wx - 15, wz - 8, wx + 15, wz + 8, CURB_H + 7, 2.5, true, 0x5a5e62, 0.5);
    ctx.colliders.push(aabb(wx - 15, wz - 8, wx + 15, wz + 8, 8));
    ctx.buildingRects.push(aabb(wx - 15, wz - 8, wx + 15, wz + 8, 8));
  }
  ctx.signRequests.push({ x: b.x0 + 22, y: CURB_H + 7.8, z: b.z1 - 17.5, ry: Math.PI, w: 16, idx: 7 });
  // container stacks
  const COL = [0xb0342a, 0x2a5aa8, 0x3a8a3a, 0xd08a20, 0x707070, 0x8a2a8a, 0xc8c8c8];
  for (let r = 0; r < 2; r++) {
    for (let c = 0; c < 7; c++) {
      const x = b.x0 + 24 + c * 7;
      const z = b.z0 + 8 + r * 9;
      const n = 1 + Math.floor(rng() * 3);
      for (let k = 0; k < n; k++) ctx.containerRequests.push({ x, y: CURB_H + k * 2.6, z, ry: 0, color: COL[Math.floor(rng() * COL.length)] });
      ctx.colliders.push(aabb(x - 3.1, z - 1.3, x + 3.1, z + 1.3, n * 2.6));
    }
  }
  // loose containers
  for (let i = 0; i < 5; i++) {
    const x = b.x0 + 80 + i * 8, z = b.z0 + 14 + (i % 2) * 6;
    ctx.containerRequests.push({ x, y: CURB_H, z, ry: (rng() - 0.5) * 0.5, color: COL[Math.floor(rng() * COL.length)] });
    ctx.colliders.push(aabb(x - 3.4, z - 2, x + 3.4, z + 2, 2.6));
  }
  // gantry cranes by the water (west edge)
  for (const z of [cz - 16, cz + 14]) {
    const x = b.x0 + 6;
    const yel = 0xd6a020;
    for (const [dx, dz] of [[-3, -3], [3, -3], [-3, 3], [3, 3]]) {
      ctx.metal.box(x + dx, CURB_H, z + dz, 1, 20, 1, yel);
      ctx.colliders.push(aabb(x + dx - 0.6, z + dz - 0.6, x + dx + 0.6, z + dz + 0.6, 20));
    }
    ctx.metal.box(x, CURB_H + 20, z, 8, 1.6, 1.6, yel);
    ctx.metal.box(x - 12, CURB_H + 20.4, z, 32, 1, 1, yel);
    ctx.metal.box(x, CURB_H + 21.6, z, 2.4, 2, 2.4, 0x50545a);
    ctx.metal.box(x - 24, CURB_H + 12, z, 0.1, 8.4, 0.1, 0x222222);
    ctx.metal.box(x - 24, CURB_H + 11, z, 1.5, 1, 1.5, 0x50545a);
    ctx.metal.box(x + 8, CURB_H, z, 3, 3, 3, 0x50545a);
  }
  // bollards on the quay edge + a moored barge
  for (let z = b.z0 + 2; z < b.z1; z += 8) ctx.metal.cyl(b.x0 - 1.2, CURB_H, z, 0.22, 0.28, 0.8, 0x222222, 8);
  const bx = L.waterX1 - 6;
  ctx.solid.box(bx, WATER_Y - 0.4, cz - 4, 9, 2.2, 34, 0x3a3f44);
  ctx.solid.box(bx, WATER_Y + 1.8, cz - 12, 8, 0.4, 16, 0x8a3a2a);
  ctx.solid.box(bx, WATER_Y + 1.8, cz + 8, 6, 3, 5, 0xe8e2d2);
  ctx.buildingRects.push(aabb(b.x0 + 20, b.z0 + 5, b.x0 + 70, b.z0 + 22, 3));
  addPoi(ctx, b, b.landmark!);
}

// ---------------------------------------------------------------- hospital ----

function hospital(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  const hx0 = cx - 20, hx1 = cx + 20, hz0 = cz - 16, hz1 = cz + 12;
  const h = addBuildingBox(ctx, hx0, hz0, hx1, hz1, { style: F.white, shops: false, floors: 6, tint: tintOf(0xffffff), plain: true });
  // entrance canopy + glass doors on the south
  ctx.glass.box(cx, CURB_H, hz1 + 0.15, 12, 4, 0.3, 0x8fb3cc);
  ctx.solid.box(cx, 4.2, hz1 + 3.5, 14, 0.5, 7, 0xe8e8e4);
  for (const dx of [-6, 6]) ctx.metal.cyl(cx + dx, CURB_H, hz1 + 6.5, 0.15, 0.15, 4.2, 0x8a9aa8, 8);
  // red cross on the facade + roof
  ctx.redLit.box(cx, 12, hz1 + 0.2, 1.2, 4.2, 0.25, 0xff2020);
  ctx.redLit.box(cx, 13.5, hz1 + 0.2, 4.2, 1.2, 0.25, 0xff2020);
  ctx.metal.box(cx, h, hz1 - 3, 0.4, 5, 0.4, 0x8a9aa8);
  ctx.redLit.box(cx, h + 5, hz1 - 3, 1.0, 4, 0.3, 0xff2020);
  ctx.redLit.box(cx, h + 6.5, hz1 - 3, 4, 1.0, 0.3, 0xff2020);
  ctx.signRequests.push({ x: cx, y: 6.5, z: hz1 + 0.25, ry: 0, w: 12, idx: 0 });
  // helipad
  ctx.solid.cyl(cx - 8, h + 0.6, cz - 4, 6, 6, 0.15, 0x50545a, 24);
  ctx.solid.box(cx - 9.5, h + 0.75, cz - 4, 0.6, 0.05, 4.5, 0xf0f0f0);
  ctx.solid.box(cx - 6.5, h + 0.75, cz - 4, 0.6, 0.05, 4.5, 0xf0f0f0);
  ctx.solid.box(cx - 8, h + 0.75, cz - 4, 3, 0.05, 0.6, 0xf0f0f0);
  // ambulance bay (east) with a striped ramp + small pharmacy building (north-west)
  ctx.solid.box(hx1 + 5, CURB_H, hz1 - 2, 8, 0.15, 12, 0xc8c8c4);
  for (let i = 0; i < 6; i++) ctx.solid.box(hx1 + 5, CURB_H + 0.15, hz1 - 7 + i * 2, 8, 0.03, 0.6, i % 2 ? 0xd02020 : 0xf0f0f0);
  ctx.treeRequests.push({ x: hx0 - 4, z: hz1 + 2, s: 1 }, { x: hx1 + 4, z: hz0 - 2, s: 1 }, { x: hx0 - 4, z: hz0 + 2, s: 0.9 });
  addPoi(ctx, b, b.landmark!);
}

// ------------------------------------------------------------------ police ----

function police(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2;
  const F = ctx.tex.facades;
  const hx0 = cx - 18, hx1 = cx + 14, hz0 = cz - 14, hz1 = cz + 12;
  const h = addBuildingBox(ctx, hx0, hz0, hx1, hz1, { style: F.stone, shops: false, floors: 4, tint: tintOf(0xb8c4d0) });
  // portico with columns + steps
  const pcx = (hx0 + hx1) / 2;
  for (let i = 0; i < 3; i++) ctx.solid.box(pcx, CURB_H, hz1 + 1 + i * 0.8, 14 - i, 1.0 - i * 0.3, 0.8, 0xc8c8c4);
  for (let i = 0; i < 4; i++) ctx.solid.cyl(pcx - 4.5 + i * 3, CURB_H + 1, hz1 + 2.5, 0.45, 0.5, 6, 0xd8d8d4, 10);
  ctx.solid.box(pcx, CURB_H + 7, hz1 + 1.5, 13, 0.8, 4.5, 0xc8c8c4);
  ctx.blueLit.sphere(pcx - 6, CURB_H + 3.2, hz1 + 4, 0.35, 0x3a7dff, 10);
  ctx.blueLit.sphere(pcx + 6, CURB_H + 3.2, hz1 + 4, 0.35, 0x3a7dff, 10);
  ctx.metal.cyl(pcx - 6, CURB_H, hz1 + 4, 0.08, 0.1, 3, 0x333333, 6);
  ctx.metal.cyl(pcx + 6, CURB_H, hz1 + 4, 0.08, 0.1, 3, 0x333333, 6);
  ctx.signRequests.push({ x: pcx, y: CURB_H + 9.6, z: hz1 + 0.25, ry: 0, w: 10, idx: 1 });
  // antenna mast + flag
  ctx.metal.cyl(hx1 - 4, h, hz0 + 4, 0.12, 0.25, 12, 0x8a8f96, 6);
  flagPole(ctx, hx0 + 3, hz1 + 5, 9);
  // yard with parking spots (east side)
  ctx.metal.box(hx1 + 9, CURB_H, hz0 + 2, 12, 1.8, 0.1, 0x555a60);
  for (let i = 0; i < 4; i++) ctx.parking.push({ pos: new THREE.Vector3(hx1 + 4 + i * 3, 0, cz + 2), heading: Math.PI });
  ctx.colliders.push(aabb(hx1 + 3, hz0 + 1.9, hx1 + 15, hz0 + 2.1, 1.8));
  addPoi(ctx, b, b.landmark!, -2);
}

// ---------------------------------------------------------------- apartment ----

function apartment(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2;
  const F = ctx.tex.facades;
  const rng = ctx.rng;
  // the player's house: classic, warm tint, green door
  const ax0 = cx - 12, ax1 = cx + 12, az0 = b.z1 - 18, az1 = b.z1;
  addBuildingBox(ctx, ax0, az0, ax1, az1, { style: F.classic, shops: true, floors: 5, tint: tintOf(0xffe9c9), streetSides: 'S' });
  ctx.solid.box(cx, CURB_H, az1 + 0.25, 2.4, 3.4, 0.2, 0x2f5a3a);
  ctx.solid.box(cx, CURB_H + 3.4, az1 + 0.4, 3.2, 0.3, 0.8, 0xefe4cc);
  ctx.lit.sphere(cx, CURB_H + 3.2, az1 + 0.5, 0.18, 0xffe0a0, 8);
  // neighbours
  addBuildingBox(ctx, b.x0, az0 + 1, ax0 - 1.2, az1, { style: F.terracotta, shops: true, floors: 4, streetSides: 'SW' });
  addBuildingBox(ctx, ax1 + 1.2, az0 + 1, b.x1, az1, { style: F.brick, shops: true, floors: 4, streetSides: 'SE' });
  addBuildingBox(ctx, b.x0, b.z0, cx - 2, b.z0 + 16, { style: F.classic, shops: rng() < 0.7, floors: 5, streetSides: 'NW' });
  addBuildingBox(ctx, cx + 1, b.z0, b.x1, b.z0 + 16, { style: F.panel, shops: false, floors: 6, streetSides: 'NE' });
  // courtyard
  for (let i = 0; i < 3; i++) ctx.treeRequests.push({ x: cx - 8 + i * 8, z: b.z0 + 25, s: 1 });
  ctx.benchRequests.push({ x: cx, z: b.z0 + 30, ry: 0 });
  addPoi(ctx, b, b.landmark!);
}

// ------------------------------------------------------------------ garage ----

function garage(ctx: BuildCtx, b: Block): void {
  const cx = (b.x0 + b.x1) / 2;
  const F = ctx.tex.facades;
  const rng = ctx.rng;
  const gx0 = b.x0, gx1 = b.x0 + 26, gz0 = b.z1 - 18, gz1 = b.z1;
  const gh = 6;
  const conc = 0x9a9892;
  ctx.solid.box((gx0 + gx1) / 2, CURB_H, gz0 + 0.25, gx1 - gx0, gh, 0.5, conc);
  ctx.solid.box(gx0 + 0.25, CURB_H, (gz0 + gz1) / 2, 0.5, gh, gz1 - gz0, conc);
  ctx.solid.box(gx1 - 0.25, CURB_H, (gz0 + gz1) / 2, 0.5, gh, gz1 - gz0, conc);
  ctx.solid.box((gx0 + gx1) / 2, CURB_H + gh, (gz0 + gz1) / 2, gx1 - gx0 + 0.6, 0.6, gz1 - gz0 + 0.6, 0x6b6a66);
  ctx.solid.box((gx0 + gx1) / 2, CURB_H + gh - 1.4, gz1 - 0.3, gx1 - gx0, 1.4, 0.6, 0x3a3f44);
  ctx.solid.box(gx0 + 8, CURB_H, gz1 - 0.3, 0.6, gh, 0.6, conc);
  ctx.solid.box(gx1 - 8, CURB_H, gz1 - 0.3, 0.6, gh, 0.6, conc);
  ctx.lit.box((gx0 + gx1) / 2, CURB_H + gh - 0.25, (gz0 + gz1) / 2, gx1 - gx0 - 1.2, 0.12, gz1 - gz0 - 1.2, 0xfff2d0);
  // interior: car lift, workbench, tyres, drums
  ctx.metal.box(gx0 + 7, CURB_H, gz0 + 8, 3.2, 0.3, 6, 0xd6a020);
  ctx.metal.box(gx0 + 5.6, CURB_H, gz0 + 8, 0.4, 2.2, 6, 0xd6a020);
  ctx.metal.box(gx0 + 8.4, CURB_H, gz0 + 8, 0.4, 2.2, 6, 0xd6a020);
  ctx.solid.box(gx1 - 4, CURB_H, gz0 + 2, 6, 0.9, 1.2, 0x5a4a3a);
  ctx.metal.box(gx1 - 4, CURB_H + 0.9, gz0 + 2, 6.2, 0.08, 1.4, 0x8a8f96);
  ctx.solid.box(gx1 - 4, CURB_H + 1.5, gz0 + 0.7, 6, 2.5, 0.3, 0xb0342a);
  for (let i = 0; i < 4; i++) ctx.metal.add(new THREE.TorusGeometry(0.42, 0.16, 6, 14), mat(gx1 - 12 + (i % 2) * 1, CURB_H + 0.16 + Math.floor(i / 2) * 0.33, gz0 + 1.4, 0, 1, 1, 1, Math.PI / 2), 0x1a1a1a);
  for (let i = 0; i < 3; i++) ctx.metal.cyl(gx0 + 2 + i * 0.9, CURB_H, gz0 + 1.6, 0.32, 0.32, 0.9, [0x2a5aa8, 0xb0342a, 0x3a3f44][i], 10);
  ctx.metal.box(gx0 + 4, CURB_H, gz0 + 4, 1.2, 1.4, 0.6, 0xb0342a);
  // sign + rooftop billboard + forecourt parking
  ctx.signRequests.push({ x: (gx0 + gx1) / 2, y: CURB_H + gh - 0.7, z: gz1 + 0.05, ry: 0, w: 12, idx: 2 });
  ctx.billboardRequests.push({ x: gx0 + 13, y: CURB_H + gh + 3.6, z: gz1 - 1.5, ry: 0, w: 10, idx: 5 });
  for (let i = 0; i < 3; i++) ctx.parking.push({ pos: new THREE.Vector3(gx1 + 6 + i * 3.2, 0, gz1 - 5), heading: Math.PI });
  ctx.colliders.push(aabb(gx0, gz0, gx1, gz0 + 0.5, gh));
  ctx.colliders.push(aabb(gx0, gz0, gx0 + 0.5, gz1, gh));
  ctx.colliders.push(aabb(gx1 - 0.5, gz0, gx1, gz1, gh));
  ctx.colliders.push(aabb(gx0 + 5.4, gz0 + 5, gx0 + 8.6, gz0 + 11, 2.2));
  ctx.colliders.push(aabb(gx1 - 7, gz0 + 0.5, gx1 - 1, gz0 + 2.6, 1));
  ctx.buildingRects.push(aabb(gx0, gz0, gx1, gz1, gh));
  // rest of the block
  addBuildingBox(ctx, b.x0, b.z0, cx - 1, b.z0 + 20, { style: F.brick, shops: rng() < 0.6, floors: 4, streetSides: 'NW' });
  addBuildingBox(ctx, cx + 1, b.z0, b.x1, b.z0 + 20, { style: F.classic, shops: true, floors: 5, streetSides: 'NE' });
  addBuildingBox(ctx, b.x1 - 16, b.z0 + 22, b.x1, b.z1, { style: F.panel, shops: false, floors: 5, streetSides: 'SE' });
  ctx.pois.push({ key: 'garage', name: "Zoli's Garage", pos: new THREE.Vector3((gx0 + gx1) / 2, 0, b.z1 + SIDEWALK / 2), heading: Math.PI });
}

// ----------------------------------------------------------------- bridges ----

function bridges(ctx: BuildCtx): void {
  const L = ctx.layout;
  const cx = L.riverCx;
  const deckX0 = L.roadX[L.riverCol] + HALF + SIDEWALK; // promenade inner edges
  const deckX1 = L.roadX[L.riverCol + 1] - HALF - SIDEWALK;
  const span = deckX1 - deckX0;
  const railColl = (Z: number, colour: number) => {
    for (const dz of [-(HALF + SIDEWALK), HALF + SIDEWALK]) {
      const z = Z + dz;
      ctx.metal.box(cx, CURB_H, z, span, 1.1, 0.14, colour);
      ctx.metal.box(cx, CURB_H + 1.0, z, span, 0.12, 0.28, colour);
      for (let x = deckX0 + 2; x < deckX1; x += 4) ctx.metal.box(x, CURB_H, z, 0.12, 1.0, 0.12, colour);
      ctx.colliders.push(aabb(deckX0, z - 0.25, deckX1, z + 0.25, 1.2));
    }
  };
  const deck = (Z: number, colour: number) => {
    ctx.solid.box(cx, -1.7, Z, span, 1.6, 2 * (HALF + SIDEWALK), colour);
  };
  // lamp posts on both sidewalks
  const lamps = (Z: number, step: number) => {
    for (let x = deckX0 + 8; x < deckX1 - 4; x += step) {
      ctx.lampRequests.push({ x, z: Z - HALF - 1.2, ry: 0 });
      ctx.lampRequests.push({ x, z: Z + HALF + 1.2, ry: Math.PI });
    }
  };

  // ---- Chain Bridge (row 5) ------------------------------------------------------
  {
    const Z = L.roadZ[5];
    deck(Z, 0x6f6a62);
    railColl(Z, 0x3a3d42);
    lamps(Z, 16);
    const towerX = [cx - 22, cx + 22];
    const TH = 34;
    for (const tx of towerX) {
      // pier
      ctx.stoneLit.box(tx, -5, Z, 12, 5 + CURB_H, 30, C.stoneGrey);
      // arch tower (shape x -> world z)
      ctx.stoneLit.add(archWall(27, TH, 7, [{ x: 0, w: 21.5, h: 24 }]), mat(tx + 3.5, 0, Z, -Math.PI / 2), 0xb9b2a4);
      ctx.stoneLit.box(tx, TH, Z, 8.2, 1.2, 28.2, 0xc8c2b4);
      for (const [dx, dz] of [[-3.2, -12.8], [3.2, -12.8], [-3.2, 12.8], [3.2, 12.8]]) ctx.stoneLit.box(tx + dx, TH + 1.2, Z + dz, 1.4, 2.2, 1.4, 0xc8c2b4);
      ctx.colliders.push(aabb(tx - 3.5, Z - 13.5, tx + 3.5, Z - 10.7, TH));
      ctx.colliders.push(aabb(tx - 3.5, Z + 10.7, tx + 3.5, Z + 13.5, TH));
      ctx.buildingRects.push(aabb(tx - 3.5, Z - 13.5, tx + 3.5, Z + 13.5, TH));
    }
    // chains: parabolic between anchors / tower tops
    const chainY = (x: number): number => {
      const anchorA = deckX0 - 2, anchorB = deckX1 + 2;
      const top = TH + 0.8, low = 11.5, anchorH = 1.6;
      const seg = (x0: number, y0: number, x1: number, y1: number, sag: number) => {
        const t = (x - x0) / (x1 - x0);
        return y0 + (y1 - y0) * t - sag * 4 * t * (1 - t);
      };
      if (x < towerX[0]) return seg(anchorA, anchorH, towerX[0], top, 4);
      if (x < towerX[1]) return seg(towerX[0], top, towerX[1], top, top - low);
      return seg(towerX[1], top, anchorB, anchorH, 4);
    };
    for (const dz of [-(HALF + SIDEWALK - 0.4), HALF + SIDEWALK - 0.4]) {
      const pts: THREE.Vector3[] = [];
      for (let x = deckX0 - 2; x <= deckX1 + 2.01; x += 1.5) pts.push(new THREE.Vector3(x, chainY(x), Z + dz));
      const curve = new THREE.CatmullRomCurve3(pts, false, 'centripetal', 0.5);
      ctx.metal.add(new THREE.TubeGeometry(curve, 96, 0.42, 6, false), null, 0x2e3136);
      // hangers
      for (let x = deckX0 + 4; x < deckX1 - 3; x += 4) {
        if (Math.abs(x - towerX[0]) < 5 || Math.abs(x - towerX[1]) < 5) continue;
        const y = chainY(x);
        ctx.metal.box(x, CURB_H + 1.1, Z + dz, 0.12, y - 1.1 - CURB_H, 0.12, 0x2e3136);
      }
    }
    // stone lions on pedestals at both ends
    for (const x of [deckX0 - 3.5, deckX1 + 3.5]) {
      for (const dz of [-(HALF + SIDEWALK + 1.2), HALF + SIDEWALK + 1.2]) {
        ctx.stoneLit.box(x, CURB_H, Z + dz, 3.2, 1.6, 1.8, C.stoneGrey);
        ctx.stoneLit.box(x, CURB_H + 1.6, Z + dz, 2.4, 1.1, 1.2, 0xb9b2a4);
        ctx.stoneLit.sphere(x + (x < cx ? 1.2 : -1.2), CURB_H + 3.0, Z + dz, 0.65, 0xb9b2a4, 10);
        ctx.colliders.push(aabb(x - 1.6, Z + dz - 0.9, x + 1.6, Z + dz + 0.9, 3));
      }
    }
    ctx.pois.push({ key: 'bridge', name: 'Chain Bridge', pos: new THREE.Vector3(deckX1 - 6, 0, Z - HALF - SIDEWALK / 2), heading: -Math.PI / 2 });
  }

  // ---- north bridge (row 2): white cable-stayed -------------------------------------
  {
    const Z = L.roadZ[2];
    deck(Z, 0x8a8d90);
    railColl(Z, 0xe8e8e4);
    lamps(Z, 20);
    for (const px of [cx - 17, cx + 17]) {
      ctx.solid.box(px, -5, Z, 6, 5 + CURB_H, 24, 0x8a8d90);
      for (const dz of [-(HALF + SIDEWALK + 0.6), HALF + SIDEWALK + 0.6]) {
        ctx.metal.box(px, CURB_H, Z + dz, 1.6, 30, 1.2, 0xe8e8e4);
        for (let i = 1; i <= 5; i++) {
          const ax = px + (px < cx ? -1 : 1) * i * 5.5;
          const dx = ax - px, dy = 27 - 1.5;
          const len = Math.hypot(dx, dy);
          ctx.metal.add(new THREE.CylinderGeometry(0.05, 0.05, len, 4), mat(px + dx / 2, CURB_H + 1.5 + dy / 2, Z + dz, 0, 1, 1, 1, 0, -Math.atan2(dx, dy)), 0xe8e8e4);
          const bx2 = px - (px < cx ? -1 : 1) * i * 5.5;
          ctx.metal.add(new THREE.CylinderGeometry(0.05, 0.05, len, 4), mat(px + (bx2 - px) / 2, CURB_H + 1.5 + dy / 2, Z + dz, 0, 1, 1, 1, 0, -Math.atan2(bx2 - px, dy)), 0xe8e8e4);
        }
      }
      ctx.colliders.push(aabb(px - 0.8, Z - HALF - SIDEWALK - 1.2, px + 0.8, Z - HALF - SIDEWALK, 30));
      ctx.colliders.push(aabb(px - 0.8, Z + HALF + SIDEWALK, px + 0.8, Z + HALF + SIDEWALK + 1.2, 30));
    }
  }

  // ---- south bridge (row 8): green steel arch ---------------------------------------
  {
    const Z = L.roadZ[8];
    deck(Z, 0x6f6a62);
    railColl(Z, 0x2f6b4f);
    lamps(Z, 20);
    for (const px of [cx - 24, cx + 24]) ctx.solid.box(px, -5, Z, 6, 5 + CURB_H, 24, 0x7a766e);
    for (const dz of [-(HALF + SIDEWALK - 0.3), HALF + SIDEWALK - 0.3]) {
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 24; i++) {
        const t = i / 24;
        const x = deckX0 + span * t;
        const y = CURB_H + 1 + 22 * Math.sin(t * Math.PI);
        pts.push(new THREE.Vector3(x, y, Z + dz));
      }
      const curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.5);
      ctx.metal.add(new THREE.TubeGeometry(curve, 48, 0.5, 6, false), null, 0x2f6b4f);
      for (let i = 2; i < 23; i += 2) {
        const p = pts[i];
        ctx.metal.box(p.x, CURB_H + 1.1, p.z, 0.16, p.y - 1.1 - CURB_H, 0.16, 0x2f6b4f);
      }
      // top ornaments (turul-style finials)
      ctx.metal.sphere(cx, CURB_H + 23.6, Z + dz, 0.7, 0xd4b060, 8);
    }
    ctx.metal.box(cx, CURB_H + 22.6, Z, 6, 0.4, 2 * (HALF + SIDEWALK), 0x2f6b4f);
  }
}

// ------------------------------------------------------------------ export ----

export function buildLandmarks(ctx: BuildCtx): void {
  parliament(ctx, block(ctx, 'parliament'));
  castle(ctx, block(ctx, 'castle'));
  basilica(ctx, block(ctx, 'basilica'));
  market(ctx, block(ctx, 'market'));
  station(ctx, block(ctx, 'station'));
  ruinbar(ctx, block(ctx, 'ruinbar'));
  bath(ctx, block(ctx, 'bath'));
  stadium(ctx, block(ctx, 'stadium'));
  docks(ctx, block(ctx, 'docks'));
  hospital(ctx, block(ctx, 'hospital'));
  police(ctx, block(ctx, 'police'));
  apartment(ctx, block(ctx, 'apartment'));
  garage(ctx, block(ctx, 'garage'));
  bridges(ctx);
}
